import React, { useEffect, useState, useCallback } from "react";

import ChannelAddForm from "../../../../components/ChannelAddForm/ChannelAddForm";
import { StepIntro, StepResult } from "./steps";
import { notify } from "../../../../../../shared/utils/utils";

import { embeddedWhatsAppSignup } from "../../../../../../services/api/channels/add/whatsapp-api";

const POPUP_TIMEOUT_MS = 8000;
const FLOW_TIMEOUT_MS = 90000;

export default function CreateIntegrationEmbeddedPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [sessionInfo, setSessionInfo] = useState(null);
  const [signupCode, setSignupCode] = useState(null);

  const popupTimeoutRef = React.useRef(null);
  const flowTimeoutRef = React.useRef(null);
  const popupInteractionDetectedRef = React.useRef(false);

  const clearPopupTimeout = useCallback(() => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  }, []);

  const clearFlowTimeout = useCallback(() => {
    if (flowTimeoutRef.current) {
      clearTimeout(flowTimeoutRef.current);
      flowTimeoutRef.current = null;
    }
  }, []);

  const resetFlow = useCallback(() => {
    clearPopupTimeout();
    clearFlowTimeout();
    setCurrentStep(1);
    setLoading(false);
    setSuccess(false);
    setSessionInfo(null);
    setSignupCode(null);
  }, [clearFlowTimeout, clearPopupTimeout]);

  const startPopupTimeout = useCallback(() => {
    clearPopupTimeout();
    popupTimeoutRef.current = setTimeout(() => {
      if (popupInteractionDetectedRef.current) return;
      setLoading(false);
      notify(
        "Alert",
        "Não detectamos a abertura do popup da Meta. Verifique bloqueador de popups e tente novamente.",
        true
      );
    }, POPUP_TIMEOUT_MS);
  }, [clearPopupTimeout]);

  const startFlowTimeout = useCallback(() => {
    clearFlowTimeout();
    flowTimeoutRef.current = setTimeout(() => {
      setLoading(false);
      setSuccess(false);
      setSessionInfo(null);
      setSignupCode(null);
      notify(
        "Alert",
        "A Meta demorou para responder. Tente novamente sem recarregar a página.",
        true
      );
    }, FLOW_TIMEOUT_MS);
  }, [clearFlowTimeout]);

  useEffect(() => {
    const handler = (event) => {
      if (!event.origin.includes("facebook.com")) return;
      popupInteractionDetectedRef.current = true;
      clearPopupTimeout();

      let data = event.data;

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (data?.type === "WA_EMBEDDED_SIGNUP") {
        setSessionInfo(data);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [clearPopupTimeout]);

  useEffect(() => {
    const handleWindowBlur = () => {
      popupInteractionDetectedRef.current = true;
      clearPopupTimeout();
    };

    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, [clearPopupTimeout]);

  useEffect(() => {
    if (!sessionInfo || !signupCode) return;

    let isMounted = true;

    const sendToBackend = async () => {
      setLoading(true);

      try {
        const payload = {
          timestamp: new Date().toISOString(),
          sessionInfo,
          code: signupCode,
        };

        await embeddedWhatsAppSignup(payload);

        if (!isMounted) return;

        setSuccess(true);
        setCurrentStep(2);
      } catch (err) {
        console.error("[WhatsApp] Erro ao integrar:", err);

        if (!isMounted) return;

        setSuccess(false);
        setCurrentStep(2);
      } finally {
        if (isMounted) {
          clearPopupTimeout();
          clearFlowTimeout();
          setLoading(false);
          setSessionInfo(null);
          setSignupCode(null);
        }
      }
    };

    sendToBackend();

    return () => {
      isMounted = false;
    };
  }, [sessionInfo, signupCode, clearFlowTimeout, clearPopupTimeout]);

  const fbLoginCallback = (response) => {
    popupInteractionDetectedRef.current = true;
    clearPopupTimeout();

    if (!response?.authResponse?.code) {
      console.warn("[FB] Fluxo cancelado ou erro no login");

      clearFlowTimeout();
      setLoading(false);
      setSuccess(false);
      return;
    }

    setSignupCode(response.authResponse.code);
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      console.error("FB SDK ainda não carregado");
      clearPopupTimeout();
      clearFlowTimeout();
      setLoading(false);
      notify(
        "Error",
        "SDK da Meta indisponível no momento. Aguarde alguns segundos e tente novamente.",
        true
      );
      return;
    }

    window.FB.login(fbLoginCallback, {
      config_id: "802606645461042",
      response_type: "code",
      override_default_response_type: true,
      extras: { version: "v3" },
    });
  };

  const handleAdd = () => {
    if (loading) return;

    popupInteractionDetectedRef.current = false;
    setSuccess(false);
    setSessionInfo(null);
    setSignupCode(null);
    setLoading(true);
    startFlowTimeout();
    startPopupTimeout();
    launchWhatsAppSignup();
  };

  useEffect(() => {
    return () => {
      clearPopupTimeout();
      clearFlowTimeout();
    };
  }, [clearFlowTimeout, clearPopupTimeout]);

  return (
    <ChannelAddForm
      steps={[
        {
          title: "Vamos conectar seu WhatsApp Business?",
          description: <StepIntro />,
        },
        {
          title: success
            ? "WhatsApp conectado com sucesso!"
            : "Ops! Algo deu errado",
          description: <StepResult success={success} />,
        },
      ]}
      success={success}
      loading={loading}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={handleAdd}
      disableBack={loading}
      onRetry={resetFlow} 
    />
  );
}
